import { useEffect, useId, type CSSProperties } from "react";
import {
  resolveBodyColor,
  resolveBodyFontFamily,
  resolveBodyFontSize,
  resolveHeadingColor,
  resolveHeadingFontFamily,
  resolveHeadingFontSize,
  resolveHyperlinkFontColor,
  resolveHyperlinkHoverColor,
  resolveHyperlinkUnderline,
} from "../theme/themeValueResolver";
import { useWBTheme } from "../theme/WBThemeRuntimeProvider";
import { useWBLang } from "../lang/WBLanguageRuntimeProvider";
import { resolveText, type LangText } from "../lib/resolve-text";
import {
  type Optional,
  type BorderSide,
  DEFAULT_TEXT,
  classNames,
  extractUtilityClasses,
  getBorderClasses,
  toCssLength,
  textAlign,
  mapOverviewLabelToVerification,
  sanitizeHtml,
  useSubmitState,
} from "./shared";
import {
  parsePx,
  resolveFluidFontSize,
  resolveScaledFontSizePx,
} from "./fontSizeScale";

export function TextChild({
  width,
  isVisible = true,
  text,
  defaultText,
  submittedText,
  isSubmitted = false,
  enableSubmittedState = false,
  fontSize,
  fluid,
  fontFamily,
  textAlign: textAlignProp,
  header,
  headingLevel,
  color,
  linkColor,
  border = false,
  borderColor,
  borderWidth,
  borderStyle,
  borderSides,
  borderRadius,
  padding,
}: {
  width?: Optional<string>;
  isVisible?: Optional<boolean>;
  text?: LangText;
  defaultText?: Optional<string>;
  submittedText?: LangText;
  isSubmitted?: Optional<boolean>;
  enableSubmittedState?: Optional<boolean>;
  fontSize?: Optional<string>;
  // Fluid display type: render the resolved px size as a viewport clamp()
  // (undefined = current fixed behavior, byte-identical)
  fluid?: Optional<boolean>;
  fontFamily?: Optional<string>;
  textAlign?: Optional<"left" | "center" | "right" | "justify">;
  header?: Optional<boolean>;
  headingLevel?: Optional<"h1" | "h2">;
  color?: Optional<string>;
  linkColor?: Optional<string>;
  border?: Optional<boolean>;
  borderColor?: Optional<string>;
  borderWidth?: Optional<string>;
  borderStyle?: Optional<string>;
  borderSides?: BorderSide[];
  borderRadius?: Optional<string>;
  padding?: Optional<string>;
}) {
  const {
    currentFonts,
    currentColorPalette,
    currentRounding,
    bodyTheme,
    headingsTheme,
    hyperlinkTheme,
  } = useWBTheme();
  const { currentLang, primaryLanguage } = useWBLang();
  const instanceId = useId().replace(/:/g, "");
  // Audit 2026-09-23 (4b): the owning form's live state beats the stored
  // `isSubmitted` seed (see SubmitStateContext).
  const submitState = useSubmitState();

  // NO early return here. The link-style `useEffect` below is a real hook slot,
  // so skipping it on a hidden render changed the hook count and React threw
  // "Rendered fewer hooks than expected" — a white screen the moment a
  // visibilityFilter hid this node mid-form. The guard now sits just above the
  // JSX return; the effect opts out through its own body instead.

  // Resolve heading level: explicit headingLevel takes priority, then legacy header boolean → h1
  const resolvedLevel = headingLevel ?? (header ? "h1" : undefined);
  const textSizeClasses = extractUtilityClasses(fontSize, "text-");
  const defaultFontSize = resolvedLevel
    ? resolveHeadingFontSize({
        level: resolvedLevel,
        headingsTheme,
      })
    : resolveBodyFontSize({ bodyTheme });
  const explicitFontSize = toCssLength(fontSize);
  const resolvedLinkColor = resolveHyperlinkFontColor({
    override: linkColor,
    hyperlinkTheme,
    palette: currentColorPalette,
    allowInherit: true,
  });
  const resolvedLinkHoverColor = resolveHyperlinkHoverColor({
    hyperlinkTheme,
    palette: currentColorPalette,
  });
  const resolvedLinkUnderline = resolveHyperlinkUnderline(hyperlinkTheme);

  useEffect(() => {
    // Hidden nodes render no `.text-instance-*` element, so injecting the rule
    // would leave a dangling <style> per hidden text child.
    if (!isVisible) return;
    const styleId = `text-link-styles-${instanceId}`;
    let style = document.getElementById(styleId) as HTMLStyleElement | null;
    if (!style) {
      style = document.createElement("style");
      style.id = styleId;
      document.head.appendChild(style);
    }

    style.textContent = `
      .text-instance-${instanceId} a {
        color: ${resolvedLinkColor};
        transition: color 0.2s, text-decoration 0.2s;
        ${resolvedLinkUnderline ? "text-decoration: underline;" : "text-decoration: none;"}
      }
      .text-instance-${instanceId} a:hover {
        color: ${resolvedLinkHoverColor} !important;
        text-decoration: underline !important;
      }
    `;

    return () => {
      style?.remove();
    };
  }, [
    instanceId,
    isVisible,
    resolvedLinkColor,
    resolvedLinkHoverColor,
    resolvedLinkUnderline,
  ]);

  const style: CSSProperties = {
    color: resolvedLevel
      ? resolveHeadingColor({
          override: color,
          level: resolvedLevel,
          headingsTheme,
          palette: currentColorPalette,
        })
      : resolveBodyColor({
          override: color,
          bodyTheme,
          palette: currentColorPalette,
        }),
    textAlign: textAlign(textAlignProp),
    fontFamily: resolvedLevel
      ? resolveHeadingFontFamily({
          override: fontFamily,
          level: resolvedLevel,
          headingsTheme,
          fonts: currentFonts,
        })
      : resolveBodyFontFamily({
          override: fontFamily,
          fonts: currentFonts,
        }),
    // Fluid display type: when `fluid` is set and the size resolves to a
    // known px, render it as a viewport clamp(). The Tailwind size class is
    // kept in className so its (unitless) line-height tracks the clamped
    // font-size. fluid undefined → exactly the previous behavior.
    //
    // The clamp base is the theme-SCALED px (resolveScaledFontSizePx), NOT the
    // raw Tailwind px — the editor scales the node size by the global
    // typography control before wrapping it in clamp(), so a raw base would
    // desync published output whenever the theme deviates from the neutral
    // 36/22/16 baseline.
    fontSize: (() => {
      if (fluid) {
        const scaled = resolveScaledFontSizePx({
          // extractUtilityClasses yields "" (not undefined) for non-Tailwind
          // sizes — || so an explicit px like "40px" still reaches the scaler
          nodeSize: textSizeClasses || explicitFontSize,
          level: resolvedLevel,
          headingsTheme,
          bodyTheme,
        });
        const basePx =
          scaled !== null ? parseFloat(scaled) : parsePx(defaultFontSize);
        if (basePx !== undefined) return resolveFluidFontSize(basePx, true);
      }
      return textSizeClasses
        ? undefined
        : (explicitFontSize ?? defaultFontSize);
    })(),
  };
  const resolvedTextValue = resolveText(text, currentLang, primaryLanguage);
  const resolvedSubmittedValue = resolveText(
    submittedText,
    currentLang,
    primaryLanguage,
  );
  const baseText = resolvedTextValue || defaultText || DEFAULT_TEXT;
  const baseSubmittedText = resolvedSubmittedValue || undefined;
  const submitted = submitState
    ? submitState.isSubmitted
    : Boolean(isSubmitted && enableSubmittedState);
  const effectiveText =
    submitted && baseSubmittedText ? baseSubmittedText : baseText;
  const resolvedText =
    mapOverviewLabelToVerification(effectiveText) ?? DEFAULT_TEXT;

  const borderCls = getBorderClasses(border, borderWidth || "border", borderStyle || "border-solid", borderSides);
  const roundedCls = extractUtilityClasses(
    borderRadius ?? (border ? currentRounding : undefined),
    "rounded",
  );

  // The guard, moved down here from above the effect (see the note there).
  if (!isVisible) return null;

  return (
    <div
      className={classNames("text-component w-full", width, textSizeClasses, `text-instance-${instanceId}`, padding, borderCls, roundedCls)}
      style={{
        ...style,
        borderColor: border ? (borderColor ?? currentColorPalette.secondary) : undefined,
        borderRadius: roundedCls ? undefined : toCssLength(borderRadius ?? (border ? currentRounding : undefined)),
      }}
      dangerouslySetInnerHTML={{ __html: sanitizeHtml(resolvedText) }}
    />
  );
}
